if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: [".product__media-item"],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(
          this.selectors,
          this
        );
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === "true";
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === "true";
        this.enableMobileThumbnails =
          this.dataset.enableMobileThumbnails === "true";
        this.enableImageZoom = this.dataset.enableImageZoom === "true";
        this.enableImageZoomDesktop =
          this.dataset.enableImageZoomDesktop !== "false";
        this.enableImageZoomMobile =
          this.dataset.enableImageZoomMobile !== "false";
        this.enableImageFadeEffect = this.dataset.enableImageFadeEffect === "true";
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: "1",
          spaceBetween: mediaItemGap,
          loop: !this.enableImageFadeEffect,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector(".swiper-button-next"),
            prevEl: this.querySelector(".swiper-button-prev"),
          },
          pagination: {
            el: this.querySelector(".swiper-pagination"),
            clickable: true,
            type: "fraction",
          },
          threshold: 2,
          autoScrollOffset: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 1,
          autoScrollOffset: 4,
        };

        switch (this.mediaLayout) {
          case "vertical-carousel":
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                768: {
                  direction: "vertical",
                  slidesPerView: "auto",
                },
              },
            });
            break;
          case "slider-freemode":
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              slidesPerView: "auto",
            });
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== "object") {
          if (
            (this.enableDesktopSlider || this.enableMobileThumbnails) &&
            this.elements.thumbnails
          ) {
            this.thumbsInstance = new window.FoxTheme.Carousel(
              this.elements.thumbnails,
              this.thumbsOptions
            );
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(
            this.elements.viewer,
            this.sliderOptions,
            [FoxTheme.Swiper.Thumbs]
          );
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
          this.applyInitialMedia();
        }
      }

      applyInitialMedia() {
        const initialMediaId = this.dataset.initialMediaId;
        if (!initialMediaId || !this.sliderInstance?.slider || !this.elements?.mediaList) {
          return;
        }

        const slideIndex = [...this.elements.mediaList.children].findIndex(
          (child) =>
            !child.classList.contains("swiper-slide-duplicate") &&
            child.dataset.mediaId === String(initialMediaId)
        );
        if (slideIndex < 0) return;

        if (this.enableImageFadeEffect || this.dataset.mediaLayout === "slider-freemode") {
          this.sliderInstance.slider.slideTo(slideIndex, 0, false);
        } else {
          this.sliderInstance.slider.slideToLoop(Math.max(slideIndex - 1, 0), 0, false);
        }

        if (this.thumbsInstance?.slider) {
          this.thumbsInstance.slider.slideTo(slideIndex, 0, false);
        }

        // slideTo(..., false) skips fade handlers — keep active slide visible.
        this.syncFadeStatusToIndex(slideIndex);
        this.ensureSlideImageEager(slideIndex);
      }

      syncFadeStatusToIndex(slideIndex) {
        if (!this.enableImageFadeEffect || !this.sliderInstance?.slider) return;
        const { slides } = this.sliderInstance.slider;
        if (!slides?.length) return;

        slides.forEach((slide) => {
          if (slide?.dataset) slide.dataset.status = "";
        });
        if (slides[slideIndex]) {
          slides[slideIndex].dataset.status = "fade-in";
          slides[slideIndex].classList.add("swiper-slide-active", "fade-in");
        }
      }

      ensureSlideImageEager(slideIndex) {
        const slide =
          this.sliderInstance?.slider?.slides?.[slideIndex] ||
          this.elements?.mediaList?.children?.[slideIndex];
        const img = slide?.querySelector?.("img");
        if (!img) return;

        img.loading = "eager";
        if ("fetchPriority" in img) img.fetchPriority = "high";

        // Lazy custom elements / delayed src: force the browser to start loading now.
        if (img.dataset?.src && !img.getAttribute("src")) {
          img.src = img.dataset.src;
        }
        if (typeof img.decode === "function") {
          img.decode().catch(() => {});
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === "object") {
          this.sliderInstance.slider.destroy(true, true);
          this.sliderInstance = false;
        }
        if (typeof this.thumbsInstance === "object") {
          this.thumbsInstance.slider.destroy(true, true);
          this.thumbsInstance = false;
        }
      }

      reinitAfterDomSwap() {
        this.elements = window.FoxTheme.utils.queryDomNodes(
          this.selectors,
          this
        );
        this.onlyImage = this.dataset.onlyImage === "true";
        this.enableImageZoom = this.dataset.enableImageZoom === "true";
        this.enableImageZoomDesktop =
          this.dataset.enableImageZoomDesktop !== "false";
        this.enableImageZoomMobile =
          this.dataset.enableImageZoomMobile !== "false";
        this.destroySlider();
        this.updateMediaLayout();
        this.applyInitialMedia();

        if (this.enableImageZoom) {
          if (this.lightbox?.destroy) {
            try {
              this.lightbox.destroy();
            } catch (error) {
              // PhotoSwipe may already be torn down with the old slides.
            }
          }
          this.lightbox = null;
          this.initImageZoom();
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== "object") {
          this.thumbsInstance = new window.FoxTheme.Carousel(
            this.selectors.thumbnails,
            this.thumbsOptions
          );
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = [
          ...this.querySelectorAll(
            ".product__media-item:not(.swiper-slide-duplicate)"
          ),
        ];
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case "model":
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector("product-model").outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case "video":
              case "external_video":
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector("video-element").outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case "image":
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter("thumbEl", (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector("img");
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter(
          "placeholderSrc",
          (placeholderSrc, { data: { id } }) => {
            if (this.sliderInstance && this.sliderInstance.slider) {
              const { slides, activeIndex } = this.sliderInstance.slider;
              if (slides[activeIndex]) {
                const el = slides[activeIndex].querySelector("img");
                if (el) {
                  return el.src;
                }
              }
            }

            return placeholderSrc;
          }
        );

        this.lightbox.on("change", () => {
          window.pauseAllMedia(this);
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { currIndex } = this.lightbox.pswp;
            if (this.sliderInstance.slider.realIndex !== currIndex) {
              this.sliderInstance.slider.slideToLoop(currIndex, 100, false);
            }
          }
        });

        this.lightbox.on("pointerDown", (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != "image") {
            e.preventDefault();
          }
        });

        this.lightbox.on("uiRegister", () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: "next",
              ariaLabel: "Next slide",
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: "prev",
              ariaLabel: "Previous slide",
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: "close-zoom",
            ariaLabel: "Close zoom image",
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: ".js-photoswipe--zoom",
          context: this,
          handler: (e, media) => {
            const isMobile = window.matchMedia(
              FoxTheme.config.mediaQueryMobile
            ).matches;
            if (isMobile && !this.enableImageZoomMobile) return;
            if (!isMobile && !this.enableImageZoomDesktop) return;

            if (media.dataset.mediaType !== "image") return;

            // dataSource is built from visible slides in DOM order; open the
            // currently viewed slide, not product.media forloop index.
            let index = -1;
            if (this.sliderInstance?.slider) {
              index = this.sliderInstance.slider.realIndex;
            }
            if (index < 0) {
              const slides = [
                ...this.querySelectorAll(
                  ".product__media-item:not(.swiper-slide-duplicate)"
                ),
              ];
              const slide = media.closest(".product__media-item");
              index = slides.indexOf(slide);
            }
            if (index < 0) index = 0;

            this.lightbox.loadAndOpen(index);
          },
        });
      }

      handleFadeTransitionForChange(swiper) {
        if (!this.enableImageFadeEffect) return;

        const { slides, activeIndex } = swiper;
        if (!slides?.[activeIndex]) return;

        // Variant gallery sync: snap fade status without the 1s opacity animation.
        if (this.dataset.skipNextFade === "true") {
          delete this.dataset.skipNextFade;
          this.syncFadeStatusToIndex(activeIndex);
          this.ensureSlideImageEager(activeIndex);
          return;
        }

        const { previousIndex } = swiper;
        if (activeIndex !== previousIndex && slides[previousIndex]) {
          slides[previousIndex].dataset["status"] = "fade-out";

          requestAnimationFrame(() => {
            slides[previousIndex].dataset["status"] = "";
            slides[activeIndex].dataset["status"] = "fade-in";
            this.ensureSlideImageEager(activeIndex);
          });
        } else {
          requestAnimationFrame(() => {
            slides[activeIndex].dataset["status"] = "fade-in";
            this.ensureSlideImageEager(activeIndex);
          });
        }
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on("afterInit", (swiper) => {
          const { slides, activeIndex } = swiper;
          this.handleFadeTransitionForChange(swiper)
          if (slides[activeIndex]) {
            const isModelMediaType =
              slides[activeIndex].dataset.mediaType === "model";
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on("realIndexChange", (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;
          this.handleFadeTransitionForChange(swiper)
          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType =
              slides[activeIndex].dataset.mediaType === "model";
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector("product-model");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (
          !variant ||
          !variant.hasOwnProperty("featured_media") ||
          !variant.featured_media
        )
          return;

        if (this.sliderInstance.slider) {
          // Filtered shade galleries do not match product-wide featured_media.position.
          // Never fall back to that position or Swiper can jump to the last slide.
          let slideIndex = 0;
          if (this.elements.mediaList.children?.length) {
            const tmpIndex = [...this.elements.mediaList.children].findIndex(
              (child) => {
                return (
                  !child?.classList.contains("swiper-slide-duplicate") &&
                  (child?.dataset.mediaId ==
                    String(variant.featured_media?.id) ||
                    child?.dataset.alt === String(variant.title) ||
                    child?.dataset.alt === variant.featured_media?.alt)
                );
              }
            );
            if (tmpIndex >= 0) slideIndex = tmpIndex;
          }

          const isSliderFreeMode = this.dataset.mediaLayout === "slider-freemode";
          if (this.enableImageFadeEffect || isSliderFreeMode) {
            this.sliderInstance.slider.slideTo(slideIndex, 0, false);
          } else {
            this.sliderInstance.slider.slideToLoop(
              Math.max(slideIndex - 1, 0),
              0,
              false
            );
          }

          // slideTo(..., false) skips realIndexChange; sync thumbs + fade explicitly.
          if (this.thumbsInstance?.slider) {
            this.thumbsInstance.slider.slideTo(slideIndex, 0, false);
          }
          this.syncFadeStatusToIndex(slideIndex);
          this.ensureSlideImageEager(slideIndex);
        } else {
          this.sortMediaItems(variant);
        }
      }

      sortMediaItems(variant) {
        // Preserve Liquid / product.media order. Only scroll to the featured media.
        if (!variant?.featured_media?.id) return;

        if (!FoxTheme.config.mqlMobile) {
          const selectedMedia = this.querySelector(
            `[data-media-id="${variant.featured_media.id}"]`
          );
          if (selectedMedia) {
            window.scrollTo({
              top: selectedMedia.offsetTop,
              behavior: "smooth",
            });
          }
        }
      }
    }
  );
}
